import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Minus, Plus, Trash2, ShoppingBag, Loader2, Tag, X, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api, { money, formatApiErrorDetail, FALLBACK_IMG } from "@/lib/api";
import { toast } from "sonner";

const GUIDE_FOR = {
  "Liquid Cultures": "liquid-culture-mastery",
  "Grain Spawn": "sterile-technique-contamination-control",
  "Grow Bags & Buckets": "grow-bag-bulk-substrate-mastery",
};
const DEFAULT_GUIDE = "mushroom-cultivation-101";
const CAT_PRIORITY = ["Liquid Cultures", "Grain Spawn", "Grow Bags & Buckets"];

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, count, addBump } = useCart();
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [applied, setApplied] = useState(null);
  const [applying, setApplying] = useState(false);
  const [ebooks, setEbooks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/promo/active").then((r) => { if (r.data?.active) setPromo(r.data); }).catch(() => {});
    api.get("/products?category=Ebooks%20%26%20Guides").then((r) => setEbooks(r.data || [])).catch(() => {});
  }, []);

  // pick the single most relevant guide to offer as a 20%-off order bump
  const inCart = new Set(items.map((i) => i.slug));
  const hasBundle = inCart.has("complete-mycology-mastery-bundle");
  const physicalCats = [...new Set(items.filter((i) => i.type !== "digital").map((i) => i.category))];
  const hasPhysical = items.some((i) => i.type !== "digital");
  let candidates = CAT_PRIORITY.filter((c) => physicalCats.includes(c)).map((c) => GUIDE_FOR[c]);
  if (candidates.length === 0 && hasPhysical) candidates = [DEFAULT_GUIDE];
  const bumpSlug = candidates.find((s) => !inCart.has(s));
  const bumpGuide = !hasBundle && bumpSlug ? ebooks.find((e) => e.slug === bumpSlug) : null;

  const discount = applied ? +(subtotal * (applied.percent / 100)).toFixed(2) : 0;
  const total = +(subtotal - discount).toFixed(2);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setApplying(true);
    try {
      const { data } = await api.post("/promo/validate", { code });
      if (data.valid) { setApplied(data); toast.success(`Code ${data.code} applied — ${Math.round(data.percent)}% off`); }
      else { setApplied(null); toast.error("That code isn't valid"); }
    } catch { toast.error("Could not check that code"); }
    finally { setApplying(false); }
  };

  const useHintCode = () => { if (promo?.code) { setPromoInput(promo.code); } };

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity, bump: !!i.bump })),
        origin_url: window.location.origin,
        promo_code: applied?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="font-display text-2xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" /> Your Cart
            <span className="font-mono text-sm text-muted-foreground">({count})</span>
          </SheetTitle>
          <SheetDescription className="sr-only">Review your cart items, apply a promo code, and proceed to checkout.</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-4">
            <ShoppingBag className="w-10 h-10 text-muted-foreground" />
            <p className="text-muted-foreground">Your cart is empty.</p>
            <Button variant="outline" onClick={() => { setOpen(false); navigate("/shop"); }} data-testid="cart-browse-btn">
              Browse products
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 no-scrollbar">
              {items.map((i) => (
                <div key={i.slug} className="flex gap-4 border-b border-border pb-4" data-testid={`cart-item-${i.slug}`}>
                  <img src={i.image || FALLBACK_IMG} alt={i.name} className="w-20 h-20 object-cover border border-border" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg leading-tight truncate">{i.name}</p>
                    {i.bump ? (
                      <p className="mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-1">
                        <span className="font-mono text-sm text-primary">{money(i.price)}</span>
                        <span className="font-mono text-[11px] text-muted-foreground line-through">{money(Math.round((i.price / 0.8) * 100) / 100)}</span>
                        <span className="font-mono-label text-[8px] bg-primary/15 text-primary px-1.5 py-0.5 rounded">ADD-ON · 20% OFF</span>
                      </p>
                    ) : (
                      <p className="font-mono text-sm text-primary mt-0.5">{money(i.price)}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => setQty(i.slug, i.quantity - 1)} data-testid={`cart-dec-${i.slug}`}
                        className="w-7 h-7 border border-border flex items-center justify-center hover:bg-accent transition-colors">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="font-mono text-sm w-6 text-center" data-testid={`cart-qty-${i.slug}`}>{i.quantity}</span>
                      <button onClick={() => setQty(i.slug, i.quantity + 1)} data-testid={`cart-inc-${i.slug}`}
                        className="w-7 h-7 border border-border flex items-center justify-center hover:bg-accent transition-colors">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => remove(i.slug)} data-testid={`cart-remove-${i.slug}`}
                        className="ml-auto text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {bumpGuide && (
              <div className="border-t border-primary/30 bg-primary/5 px-6 py-4" data-testid="cart-order-bump">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span className="font-mono-label text-[10px] text-primary">Recommended add-on · 20% off</span>
                </div>
                <div className="flex items-center gap-3">
                  <img src={bumpGuide.image || FALLBACK_IMG} alt={bumpGuide.name} className="w-12 h-12 object-cover border border-border shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-[15px] leading-tight truncate">{bumpGuide.name}</p>
                    <p className="mt-0.5 flex items-center gap-2">
                      <span className="font-mono text-sm text-primary">{money(Math.round(bumpGuide.price * 0.8 * 100) / 100)}</span>
                      <span className="font-mono text-[11px] text-muted-foreground line-through">{money(bumpGuide.price)}</span>
                    </p>
                  </div>
                  <Button onClick={() => { addBump(bumpGuide); toast.success("Guide added — 20% off applied"); }}
                    data-testid="cart-add-bump" className="rounded-full font-mono-label text-[10px] px-4 h-9 shrink-0">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              </div>
            )}

            <div className="border-t border-border px-6 py-5 space-y-4">
              {promo && !applied && (
                <button type="button" onClick={useHintCode} data-testid="cart-promo-hint"
                  className="w-full flex items-center gap-2 text-left border border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 transition-colors px-3 py-2.5 rounded-md">
                  <Tag className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-[12px] leading-tight">
                    Launch offer: use code <span className="font-mono font-semibold text-primary">{promo.code}</span> for {Math.round(promo.percent)}% off
                  </span>
                  <span className="ml-auto font-mono-label text-[9px] text-muted-foreground">TAP TO ADD</span>
                </button>
              )}

              {applied ? (
                <div className="flex items-center justify-between border border-primary/40 bg-primary/5 px-3 py-2.5 rounded-md" data-testid="cart-promo-applied">
                  <span className="flex items-center gap-2 text-[12px]">
                    <Tag className="w-4 h-4 text-primary" />
                    <span className="font-mono font-semibold text-primary">{applied.code}</span>
                    <span className="text-muted-foreground">-{Math.round(applied.percent)}%</span>
                  </span>
                  <button onClick={() => { setApplied(null); setPromoInput(""); }} data-testid="cart-promo-remove"
                    className="text-muted-foreground hover:text-destructive transition-colors"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") applyPromo(); }}
                    placeholder="Promo code" data-testid="cart-promo-input"
                    className="h-10 font-mono text-sm uppercase placeholder:normal-case placeholder:font-sans" />
                  <Button variant="outline" onClick={applyPromo} disabled={applying || !promoInput.trim()}
                    data-testid="cart-promo-apply" className="h-10 rounded-full font-mono-label text-[11px] px-5">
                    {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-mono-label text-xs text-muted-foreground">Subtotal</span>
                  <span className="font-mono text-sm" data-testid="cart-subtotal">{money(subtotal)}</span>
                </div>
                {applied && (
                  <div className="flex items-center justify-between text-primary" data-testid="cart-discount">
                    <span className="font-mono-label text-xs">Discount ({Math.round(applied.percent)}%)</span>
                    <span className="font-mono text-sm">-{money(discount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1.5 border-t border-border">
                  <span className="font-mono-label text-xs text-muted-foreground">Total</span>
                  <span className="font-mono text-xl font-semibold" data-testid="cart-total">{money(total)}</span>
                </div>
              </div>

              <p className="text-[11px] text-muted-foreground">Taxes & shipping calculated at secure checkout.</p>
              <Button className="w-full h-12 rounded-full font-mono-label text-xs" onClick={checkout} disabled={loading} data-testid="cart-checkout-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Proceed to Checkout"}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
